import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message, Form, DatePicker, Select, Button } from "antd";
import { apis, AUTH_HEADER_NAME } from "common/apis";
import { IMarginTrackingUpload, IPoRow, IPoUpload } from "common/interface";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { removeProcessingMarginTracking, removeProcessingPO, setProcessingMarginTracking, setProcessingPO } from "store/po/poSlice";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";
import { getDarkStores } from "store/admin-slice/adminServicesSlice";

const UploadMarginTrackingPage = () => {
  useDocumentTitle("Upload Purchase Order");
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingMarginTracking } = useAppSelector((state) => state.po);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [deliveryDate, setDate] = useState<string>();

  useEffect(() => {
    dispatch(getDarkStores());
  }, []);

  useEffect(() => {
    const { timeUploaded } = processingMarginTracking;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingMarginTracking());
      }
    }

    if (processingMarginTracking && processingMarginTracking.key) {
      navigate(`/${routes.PO.SAVE_MARGIN_TRACKING}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingMarginTracking, navigate, dispatch]);

  useEffect(() => {
    const failed = processingMarginTracking?.failedRows?.length;

    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingMarginTracking?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IMarginTrackingUpload) => {
    dispatch(setProcessingMarginTracking(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingMarginTracking());
  };

  // useEffect(() => {
  //   return (() => {
  //     dispatch(removeProcessingPO())
  //   });
  // }, []);

  const { headerMapping } = processingMarginTracking;
  const headerMappingWithError = headerMapping + ",errors:Errors";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingMarginTracking?.successRows || [];
  const failedRows = processingMarginTracking?.failedRows || [];
  const successData = successRows?.map((row, i) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows?.map((row, i) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];
  const columns: ColumnsType<IPoRow & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: IPoRow & { key: string }) => {
        if (key === "errors") {
          const errorMessages = record.errors
            ?.map((error) => error.message)
            .join(", ");
          return <>{errorMessages}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (record: IPoRow & { key: string }) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }

    return "";
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Margin Tracking" />
        </Col>
      </Row>
      <Row>
        <Form style={{width:"80%"}}>
          <Form.Item label="Select Delivery Date" name="orderDate" required>
            <DatePicker
              onChange={(val) => setDate(val?.format("yyyy-MM-DD"))}
            />
          </Form.Item>
        </Form>
        <span className="mr-4"></span>
        <div><Button type="primary" onClick={()=>window.open("http://d69ugcdrlg41w.cloudfront.net/upload_samples/warehouse/margin_tracking_sample.csv")}>Download Sample File</Button></div>
      </Row>
    
      {(deliveryDate) && <Row>
        <Col lg={16} xs={24}>
          <AppUpload
            uploadUrl={apis.uploadMarginTracking}
            headers={headers}
            onUploaded={onUploaded}
            onUploading={onUploading}
            hasErrorRows={hasErrorRows}
            data={{ deliveryDate: deliveryDate,}}
          />
        </Col>
      </Row>}

      {!!Object.keys(processingMarginTracking).length && !processingMarginTracking.key && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data."
                description="Please correct errors and upload again."
                showIcon
                banner
              />
            </Col>
          </Row>

          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={(record) => addBorderToErrorRows(record)}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            ></Table>
          </Row>
        </>
      )}
    </>
  );
};

export default UploadMarginTrackingPage;
